import React from 'react';
import { Autocomplete, Box, InputAdornment, TextField, Typography } from '@mui/material';
import { OpenInNewRounded, SearchRounded } from '@mui/icons-material';

import { neutral, radii } from '@styles/tokens';

export interface SearchTarget {
  title: string;
  /** In-app route. */
  path?: string;
  /** External page, opened in a new tab. */
  href?: string;
  group: string;
}

interface Props {
  targets: SearchTarget[];
  onNavigate: (path: string) => void;
}

const isMac = typeof navigator !== 'undefined' && /Mac|iPhone|iPad/.test(navigator.platform);

/**
 * Header search - jump to any page the viewer can open.
 *
 * There is no search API, so this searches the one thing the app does know:
 * its own navigation. The targets are the permission-filtered nav list built
 * in MainLayout, so it can never offer a page the sidebar would hide.
 * Ctrl/⌘+K or "/" focuses it from anywhere.
 */
export const GlobalSearch: React.FC<Props> = ({ targets, onNavigate }) => {
  const inputRef = React.useRef<HTMLInputElement>(null);
  const [value, setValue] = React.useState<SearchTarget | null>(null);
  const [input, setInput] = React.useState('');
  const [open, setOpen] = React.useState(false);
  // A modal dialog (e.g. the vault unlock box) owns the screen - the search
  // list must not drop down over it.
  const dialogOpen = () => typeof document !== 'undefined' && !!document.querySelector('[role="dialog"]');

  React.useEffect(() => {
    const onKey = (e: KeyboardEvent) => {
      // Never steal focus while a modal dialog is open (e.g. the vault unlock
      // password box) - the shortcut used to pull focus up into the header
      // search and pop its list over the dialog.
      if (document.querySelector('[role="dialog"]')) return;
      const typing = (e.target as HTMLElement)?.closest('input, textarea, [contenteditable="true"]');
      if ((e.key === 'k' && (e.metaKey || e.ctrlKey)) || (e.key === '/' && !typing)) {
        e.preventDefault();
        inputRef.current?.focus();
      }
    };
    window.addEventListener('keydown', onKey);
    return () => window.removeEventListener('keydown', onKey);
  }, []);

  return (
    <Autocomplete
      options={targets}
      value={value}
      open={open}
      onOpen={() => { if (!dialogOpen()) setOpen(true); }}
      onClose={() => setOpen(false)}
      inputValue={input}
      onInputChange={(_e, v) => setInput(v)}
      groupBy={(o) => o.group}
      getOptionLabel={(o) => o.title}
      isOptionEqualToValue={(a, b) => a.title === b.title && a.group === b.group}
      onChange={(_e, target) => {
        if (target?.href) window.open(target.href, '_blank', 'noopener,noreferrer');
        else if (target?.path) onNavigate(target.path);
        setValue(null);
        setInput('');
        inputRef.current?.blur();
      }}
      noOptionsText="No matching page"
      size="small"
      autoHighlight
      blurOnSelect
      sx={{ width: { sm: 260, md: 320, lg: 380 } }}
      slotProps={{
        paper: { sx: { mt: 0.75, borderRadius: `${radii.md}px`, border: `1px solid ${neutral[200]}` } },
        // Keep the results list below the modal layer so it can never cover an
        // open dialog (MUI defaults the Autocomplete popper to z-index 1300,
        // the same layer as a Dialog, which is why it floated over the vault
        // unlock box).
        popper: { sx: { zIndex: (theme) => theme.zIndex.modal - 1 } },
      }}
      renderOption={(props, option) => {
        const { key, ...rest } = props as any;
        return (
          <Box component="li" key={key} {...rest} sx={{ gap: 1, fontSize: '0.875rem' }}>
            <Typography sx={{ flex: 1, fontSize: '0.875rem' }}>{option.title}</Typography>
            {option.href && <OpenInNewRounded sx={{ fontSize: 15, color: neutral[400] }} />}
          </Box>
        );
      }}
      renderInput={(params) => (
        <TextField
          {...params}
          inputRef={inputRef}
          placeholder="Search pages…"
          inputProps={{ ...params.inputProps, 'aria-label': 'Search pages' }}
          InputProps={{
            ...params.InputProps,
            startAdornment: (
              <InputAdornment position="start" sx={{ ml: 0.5 }}>
                <SearchRounded sx={{ fontSize: 19, color: neutral[400] }} />
              </InputAdornment>
            ),
            endAdornment: (
              <>
                {!input && (
                  <Box component="kbd" aria-hidden sx={{
                    mr: 0.5, px: 0.75, py: 0.125, fontSize: '0.68rem', fontFamily: 'inherit', fontWeight: 600,
                    color: neutral[500], border: `1px solid ${neutral[200]}`, borderRadius: '6px', backgroundColor: neutral[0],
                  }}>
                    {isMac ? '⌘K' : 'Ctrl K'}
                  </Box>
                )}
                {params.InputProps.endAdornment}
              </>
            ),
            sx: {
              borderRadius: `${radii.md}px`,
              backgroundColor: neutral[50],
              fontSize: '0.875rem',
              '& fieldset': { borderColor: neutral[200] },
              '&:hover fieldset': { borderColor: `${neutral[300]} !important` },
              '&.Mui-focused': { backgroundColor: neutral[0] },
            },
          }}
        />
      )}
    />
  );
};

export default GlobalSearch;
